import { Injectable } from '@angular/core';
import { FeathersService } from './feathers.service';
import { AuthCredentials } from '../../models/auth-credentials';
import {
  catchError,
  defer,
  finalize,
  from,
  iif,
  Observable,
  of,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { AuthResult } from '../../models/auth-result';
import { User } from '../../models/user';
import { ApiEndpoint, LocalStorageKey } from '../../enums';
import { SnackBarService } from '../snack-bar.service';
import { Router } from '@angular/router';
import { UsersService } from './users.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private feathers: FeathersService,
    private message: SnackBarService,
    private router: Router,
    private users: UsersService
  ) {}

  get isUserAuthenticated(): boolean {
    return this.feathers.feathers.authentication.authenticated;
  }

  login(credentials: AuthCredentials): Observable<any> {
    const effect = (authResult: AuthResult) => {
      if (this.feathers.feathers.authentication.authenticated) {
        localStorage.setItem(
          LocalStorageKey.RefreshToken,
          authResult.refreshToken!
        );
        localStorage.setItem(LocalStorageKey.UserId, authResult.user?._id!);
        this.feathers.isVisitor = false;
        this.feathers.initBaseListeners(authResult.user?._id!);
      }
    };
    return defer(() =>
      this.feathers
        .pipedFrom(
          this.feathers.feathers.authenticate(credentials, {
            query: { 'web/app': environment.whiteList },
          }),
          false
        )
        .pipe(
          switchMap(() => from(this.feathers.feathersREST.reAuthenticate())),
          tap(effect)
        )
        .pipe(
          catchError((err) => {
            switch (err.code) {
              case 400:
              case 401:
              case 490: {
                this.message.error(err.message);
                break;
              }
              case 405: {
                break;
              }
              case 489: {
                this.message.success(err.message);
                break;
              }
              case 491: {
                this.message.warn(err.message);
                break;
              }
              default:
                if (err.message) {
                  this.message.error(err.message);
                }
            }
            return throwError(err);
          })
        )
    );
  }

  deleteRefreshToken() {
    const token = localStorage.getItem(LocalStorageKey.RefreshToken);
    if (!token) {
      return of(false);
    }
    return this.feathers.pipedFrom(
      defer(() =>
        this.feathers.feathers.service(ApiEndpoint.RefreshTokens).remove(null, {
          query: {
            refreshToken: localStorage.getItem(LocalStorageKey.RefreshToken),
          },
        })
      )
    );
  }

  logout(): Observable<AuthResult | null> {
    const effect = () => {
      this.feathers.me$.next(undefined);
      this.feathers.isVisitor = true;
      this.feathers.feathersREST.logout();
    };
    return iif(
      () => this.feathers.feathers.authentication.authenticated,
      this.deleteRefreshToken(),
      of(false)
    ).pipe(
      catchError(() => of(true)),
      finalize(() => {
        this.feathers.feathers.authentication.removeAccessToken();
        localStorage.removeItem(LocalStorageKey.RefreshToken);
        localStorage.removeItem(LocalStorageKey.UserId);
      }),
      switchMap(() =>
        from(this.feathers.feathers.logout()).pipe(
          catchError((err) => {
            this.router.navigate(['login']).then();
            return throwError(err);
          }),
          tap(effect)
        )
      )
    );
  }

  verifyUser(token: string): Observable<any> {
    return this.feathers.post(
      ApiEndpoint.Auth,
      {
        action: 'verifySignupLong',
        value: token,
      },
      false
    );
  }

  sendResetPasswordEmail(email: string): Observable<any> {
    return this.feathers.post(ApiEndpoint.Auth, {
      action: 'sendResetPwd',
      value: {
        email,
      },
    });
  }

  resetPassword(token: string, password: string): Observable<any> {
    return this.feathers.post(ApiEndpoint.Auth, {
      action: 'resetPwdLong',
      value: {
        token,
        password,
      },
    });
  }

  registerUser(userInfo: {
    name: string;
    email: string;
    password: string;
  }): Observable<any> {
    return this.feathers
      .post(ApiEndpoint.Users, userInfo, false, {
        'web/app': environment.whiteList,
      })
      .pipe(
        catchError((err) => {
          if (err.code !== 405 && err.code !== 409) {
            this.message.error(err.message);
          }
          return throwError(err);
        })
      );
  }

  setTwoFactor(state: boolean): Observable<User | null> {
    if (!this.users.me$.value?._id) {
      return of(null);
    }
    return this.feathers.patch<User>(
      ApiEndpoint.Users,
      this.users.me$.value?._id,
      {
        twoFactor: state,
      }
    );
  }

  updateIdentity(
    identity: { email: string },
    password: string
  ): Observable<User | null> {
    if (!this.users.me$.value?._id) {
      return of(null);
    }
    return this.feathers.post(ApiEndpoint.Auth, {
      action: 'identityChange',
      value: {
        user: { email: this.users.me$.value?.email },
        password: password,
        changes: {
          ...identity,
        },
      },
    });
  }

  agreeToTerms() {
    return defer(() =>
      this.feathers.post(
        ApiEndpoint.TermsAgreed,
        {
          termsAgreed: true,
        },
        false
      )
    ).pipe(
      catchError((err) => {
        if (err.code !== 400) {
          this.message.error(err.message);
        }
        return err.code === 400 ? of(true) : throwError(err);
      })
    );
  }
}
